import React, { useMemo, useState, useEffect, useCallback } from "react";
import axiosInstance from "../api/axiosInstance";
import { useAuth } from "../context/AuthContext";
import { Alert, AutoComplete, Button, Form, Input, Modal, Table, Typography } from "antd";
import {
  CardTitle,
  DetailList,
  ListToolbar,
  PageHeader,
  RowActions,
  Section,
  SectionCard,
  StatusTag,
  tablePagination,
  useFlatPage,
} from "./../components/ui";
import { IconInfo, IconPlus } from "../components/ui/icons";
import {
  WifiOutlined,
  CarOutlined,
  CoffeeOutlined,
  SafetyOutlined,
  StarOutlined,
  PhoneOutlined,
  FireOutlined,
  ThunderboltOutlined,
  CameraOutlined,
  LaptopOutlined,
  PrinterOutlined,
  CustomerServiceOutlined,
  MedicineBoxOutlined,
  HeartOutlined,
  GiftOutlined,
  LockOutlined,
  EnvironmentOutlined,
  HomeOutlined,
  BankOutlined,
  ShopOutlined,
  RestOutlined,
  GlobalOutlined,
} from "@ant-design/icons";
import notify from "../utils/notify";
const { Text } = Typography;

/**
 * 1) Normalize & match amenity name -> icon
 * - Handles: "WiFi", "WI-FI", "Wi Fi", "wireless internet"
 */
const normalizeAmenityName = (name) =>
  String(name || "")
    .toLowerCase()
    .trim()
    .replace(/[_/\\\-]+/g, " ")
    .replace(/[^\w\s]+/g, "") // remove punctuation
    .replace(/\s+/g, " ");

const AMENITY_ICON_RULES = [
  {
    key: "wifi",
    label: "Wi-Fi / Internet",
    icon: <WifiOutlined />,
    keywords: ["wifi", "wi fi", "wi-fi", "wireless", "internet", "broadband"],
  },
  {
    key: "parking",
    label: "Parking",
    icon: <CarOutlined />,
    keywords: ["parking", "car park", "carpark", "garage", "park"],
  },
  {
    key: "breakfast",
    label: "Breakfast",
    icon: <CoffeeOutlined />,
    keywords: ["breakfast", "coffee", "tea", "continental", "buffet breakfast"],
  },
  {
    key: "restaurant",
    label: "Restaurant",
    icon: <ShopOutlined />,
    keywords: ["restaurant", "dining", "meal", "kitchen", "cafe"],
  },
  {
    key: "pool",
    label: "Pool",
    icon: <SafetyOutlined />,
    keywords: ["pool", "swimming", "swim"],
  },
  {
    key: "gym",
    label: "Gym / Fitness",
    icon: <ThunderboltOutlined />,
    keywords: ["gym", "fitness", "workout", "training"],
  },
  {
    key: "spa",
    label: "Spa / Wellness",
    icon: <HeartOutlined />,
    keywords: ["spa", "wellness", "massage", "sauna", "steam"],
  },
  {
    key: "security",
    label: "Security",
    icon: <LockOutlined />,
    keywords: ["security", "cctv", "guard", "safe", "vault"],
  },
  {
    key: "laundry",
    label: "Laundry",
    icon: <RestOutlined />,
    keywords: ["laundry", "washing", "dry cleaning", "drycleaning", "iron", "ironing"],
  },
  {
    key: "roomservice",
    label: "Room Service",
    icon: <CustomerServiceOutlined />,
    keywords: ["room service", "roomservice", "in room dining", "inroom dining"],
  },
  {
    key: "reception",
    label: "Reception / Front Desk",
    icon: <PhoneOutlined />,
    keywords: ["reception", "front desk", "frontdesk", "concierge", "24 hour desk", "24h desk"],
  },
  {
    key: "aircon",
    label: "Air Conditioning",
    icon: <GlobalOutlined />,
    keywords: ["aircon", "air conditioning", "ac", "a c", "cooling"],
  },
  {
    key: "heating",
    label: "Heating",
    icon: <FireOutlined />,
    keywords: ["heating", "heater", "warm"],
  },
  {
    key: "workspace",
    label: "Workspace",
    icon: <LaptopOutlined />,
    keywords: ["workspace", "desk", "work desk", "office", "business"],
  },
  {
    key: "printing",
    label: "Printing",
    icon: <PrinterOutlined />,
    keywords: ["printer", "printing", "print", "photocopy", "copy"],
  },
  {
    key: "medical",
    label: "First Aid / Medical",
    icon: <MedicineBoxOutlined />,
    keywords: ["first aid", "medical", "clinic", "doctor", "nurse", "pharmacy"],
  },
  {
    key: "cameras",
    label: "CCTV / Cameras",
    icon: <CameraOutlined />,
    keywords: ["camera", "cctv", "surveillance"],
  },
  {
    key: "location",
    label: "Location / Nearby",
    icon: <EnvironmentOutlined />,
    keywords: ["location", "nearby", "city center", "town", "mall", "airport"],
  },
  {
    key: "family",
    label: "Family Friendly",
    icon: <HomeOutlined />,
    keywords: ["family", "kids", "children", "child", "baby"],
  },
  {
    key: "business",
    label: "Business / Conference",
    icon: <BankOutlined />,
    keywords: ["business", "conference", "meeting", "boardroom", "seminar"],
  },
  {
    key: "gift",
    label: "Special / Complimentary",
    icon: <GiftOutlined />,
    keywords: ["gift", "complimentary", "free", "welcome", "bonus"],
  },
];

// Returns best match rule for a name
const detectAmenityRule = (name) => {
  const n = normalizeAmenityName(name);

  if (!n) return null;

  // 1) Exact keyword contains match
  for (const rule of AMENITY_ICON_RULES) {
    for (const kw of rule.keywords) {
      const kwn = normalizeAmenityName(kw);
      if (!kwn) continue;

      // word boundary-ish match: contains the phrase
      if (n.includes(kwn)) return rule;
    }
  }

  // 2) Extra fallback heuristics (optional)
  // e.g., "wi fi" already handled, but you can add regex checks here if needed.

  return null;
};

const getAmenityIcon = (name) => {
  const rule = detectAmenityRule(name);
  return rule?.icon || <StarOutlined />;
};

const AmenitiesListPage = () => {
  useFlatPage();
  const { authData } = useAuth();

  const hotelId = authData?.hotelId;

  const [amenities, setAmenities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [showInfoModal, setShowInfoModal] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingAmenity, setEditingAmenity] = useState(null);
  const [search, setSearch] = useState("");

  const [addForm] = Form.useForm();
  const [editForm] = Form.useForm();

  const addNameWatch = Form.useWatch("name", addForm);
  const editNameWatch = Form.useWatch("name", editForm);

  const amenityOptions = useMemo(() => {
    // AutoComplete options from known catalog (but users can still type custom)
    return AMENITY_ICON_RULES.map((r) => ({
      value: r.label,
      label: (
        <span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
          <span style={{ color: "var(--hms-accent)" }}>{r.icon}</span>
          <span>{r.label}</span>
          <StatusTag status="">{r.key}</StatusTag>
        </span>
      ),
    }));
  }, []);

  const fetchAmenities = useCallback(async () => {
    if (!hotelId) {
      setError("Hotel ID is not available.");
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await axiosInstance.get(`Amenities/Hotel/${hotelId}`);
      setAmenities(Array.isArray(response.data) ? response.data : []);
    } catch (err) {
      console.error("Error fetching amenities:", err);
      setError("Failed to load amenities. Please try again later.");
    } finally {
      setLoading(false);
    }
  }, [hotelId]);

  useEffect(() => {
    fetchAmenities();
  }, [fetchAmenities]);

  const handleAddAmenity = async (values) => {
    try {
      const amenityPayload = {
        name: values.name,
        hotelId,
        // Optional (recommended if you add it on backend later):
        // iconKey: detectAmenityRule(values.name)?.key ?? null,
      };

      const response = await axiosInstance.post(`Amenities/Hotel/${hotelId}`, amenityPayload);
      setAmenities([...amenities, response.data]);

      setShowAddModal(false);
      addForm.resetFields();

      notify.success({
        title: "Success",
        description: "Amenity added successfully.",
      });
    } catch (err) {
      console.error("Error adding amenity:", err);
      notify.error({
        title: "Error",
        description: "Failed to add amenity. Please try again.",
      });
    }
  };

  const handleUpdateAmenity = async (values) => {
    try {
      const amenityPayload = {
        amenityId: editingAmenity.amenityId,
        name: values.name,
        // Optional (recommended if you add it on backend later):
        // iconKey: detectAmenityRule(values.name)?.key ?? null,
      };

      await axiosInstance.put(
        `Amenities/Hotel/${hotelId}/Amenity/${editingAmenity.amenityId}`,
        amenityPayload
      );

      setAmenities(
        amenities.map((a) =>
          a.amenityId === editingAmenity.amenityId ? { ...a, name: values.name } : a
        )
      );

      setEditingAmenity(null);

      notify.success({
        title: "Success",
        description: "Amenity updated successfully.",
      });
    } catch (err) {
      console.error("Error updating amenity:", err);
      notify.error({
        title: "Error",
        description: "Failed to update amenity. Please try again.",
      });
    }
  };

  const handleDeleteAmenity = async (id) => {
    try {
      await axiosInstance.delete(`Amenities/Hotel/${hotelId}/Amenity/${id}`);
      setAmenities(amenities.filter((a) => a.amenityId !== id));

      notify.success({
        title: "Success",
        description: "Amenity deleted successfully.",
      });
    } catch (err) {
      console.error("Error deleting amenity:", err);
      notify.error({
        title: "Error",
        description: "Failed to delete amenity. Please try again.",
      });
    }
  };

  const renderDetectedTag = (name) => {
    const rule = detectAmenityRule(name);
    if (!name) return <StatusTag status="">Type to detect an icon</StatusTag>;
    if (!rule) return <StatusTag status="">Unmapped (default icon)</StatusTag>;
    return <StatusTag status="confirmed">Icon: {rule.key}</StatusTag>;
  };

  const visibleAmenities = amenities.filter((a) => !search.trim() || String(a.name || "").toLowerCase().includes(search.trim().toLowerCase()));

  const columns = [
    {
      title: "Amenity",
      dataIndex: "name",
      key: "name",
      render: (name) => (
        <span className="hms-person">
          <span className="hms-avatar" style={{ background: "var(--hms-wash)", color: "var(--hms-accent)" }}>
            {getAmenityIcon(name)}
          </span>
          <span className="hms-cell-main">{name}</span>
        </span>
      ),
    },
    {
      title: "Icon rule",
      key: "rule",
      width: 160,
      render: (_, a) => {
        const rule = detectAmenityRule(a.name);
        return rule ? <StatusTag status="confirmed">{rule.key}</StatusTag> : <span className="hms-muted">default</span>;
      },
    },
    {
      title: "",
      key: "actions",
      width: 110,
      align: "right",
      render: (_, amenity) => (
        <RowActions
          actions={[
            {
              key: "edit",
              label: "Edit amenity",
              icon: "edit",
              onClick: () => {
                setEditingAmenity(amenity);
                editForm.setFieldsValue({ name: amenity.name });
              },
            },
            {
              key: "delete",
              label: "Delete amenity",
              icon: "delete",
              danger: true,
              confirm: "Delete this amenity?",
              okText: "Delete",
              onClick: () => handleDeleteAmenity(amenity.amenityId),
            },
          ]}
        />
      ),
    },
  ];

  const amenityField = (formName, placeholder) => (
    <Form.Item
      name="name"
      label="Amenity name"
      rules={[
        { required: true, message: "Please enter an amenity name" },
        { min: 2, message: "Minimum 2 characters" },
      ]}
    >
      <AutoComplete
        options={amenityOptions}
        placeholder={placeholder}
        filterOption={(inputValue, option) => {
          const iv = normalizeAmenityName(inputValue);
          const ov = normalizeAmenityName(option?.value);
          return ov.includes(iv);
        }}
      >
        <Input prefix={<span style={{ color: "var(--hms-accent)" }}>{getAmenityIcon(formName === "add" ? addNameWatch : editNameWatch)}</span>} />
      </AutoComplete>
    </Form.Item>
  );

  return (
    <div className="hms-page am-page">
      <PageHeader
        actions={
          <>
            <Button icon={<IconInfo />} onClick={() => setShowInfoModal(true)}>
              Guide
            </Button>
            <Button type="primary" icon={<IconPlus />} onClick={() => setShowAddModal(true)}>
              Add amenity
            </Button>
          </>
        }
      />

      {error && (
        <Alert
          style={{ marginBottom: 12 }}
          type="error"
          showIcon
          title={error}
          action={<Button onClick={fetchAmenities}>Retry</Button>}
        />
      )}

      <SectionCard
        flush
        title={<CardTitle count={amenities.length}>Amenities</CardTitle>}
        extra={
          <ListToolbar
            search={{ value: search, onChange: setSearch, placeholder: "Search amenities" }}
            onRefresh={fetchAmenities}
            loading={loading}
          />
        }
      >
        <Table
          className="hms-table"
          columns={columns}
          dataSource={visibleAmenities}
          loading={loading}
          rowKey="amenityId"
          pagination={tablePagination("amenity", { hideOnSinglePage: true })}
          locale={{
            emptyText: (
              <div className="hms-empty">
                No amenities yet.{" "}
                <Button type="link" onClick={() => setShowAddModal(true)}>
                  Add the first one
                </Button>
              </div>
            ),
          }}
        />
      </SectionCard>

      <Modal
        title="Add amenity"
        open={showAddModal}
        destroyOnHidden
        onCancel={() => {
          addForm.resetFields();
          setShowAddModal(false);
        }}
        footer={null}
        width={480}
      >
        <Form form={addForm} layout="vertical" onFinish={handleAddAmenity}>
          {amenityField("add", "Type or pick an amenity, e.g. Wi-Fi")}
          <div style={{ marginTop: -6, marginBottom: 12 }}>{renderDetectedTag(addNameWatch)}</div>
          <p className="hms-modal-note">The icon is chosen automatically from keywords, so "Wi-Fi" and "Wireless internet" get the same icon.</p>
          <div className="hms-form-actions">
            <Button
              onClick={() => {
                addForm.resetFields();
                setShowAddModal(false);
              }}
            >
              Cancel
            </Button>
            <Button type="primary" htmlType="submit">
              Add amenity
            </Button>
          </div>
        </Form>
      </Modal>

      {editingAmenity && (
        <Modal title="Edit amenity" open={!!editingAmenity} destroyOnHidden onCancel={() => setEditingAmenity(null)} footer={null} width={480}>
          <Form form={editForm} onFinish={handleUpdateAmenity} layout="vertical">
            {amenityField("edit", "Type or pick an amenity")}
            <div style={{ marginTop: -6, marginBottom: 12 }}>{renderDetectedTag(editNameWatch)}</div>
            <div className="hms-form-actions">
              <Button onClick={() => setEditingAmenity(null)}>Cancel</Button>
              <Button type="primary" htmlType="submit">
                Save changes
              </Button>
            </div>
          </Form>
        </Modal>
      )}

      <Modal
        title="Amenities guide"
        open={showInfoModal}
        destroyOnHidden
        onCancel={() => setShowInfoModal(false)}
        footer={[
          <Button key="close" type="primary" onClick={() => setShowInfoModal(false)}>
            Got it
          </Button>,
        ]}
        width={520}
      >
        <p className="hms-modal-note">Manage the amenities your hotel offers.</p>
        <Section title="What you can do">
          <ul style={{ paddingLeft: 20, margin: 0 }}>
            <li>Add, rename and remove amenities</li>
            <li>Icons are picked automatically from the amenity name</li>
          </ul>
        </Section>
        <Section title="Right now">
          <DetailList items={[{ label: "Total amenities", value: amenities.length }]} />
        </Section>
      </Modal>
    </div>
  );
};

export default AmenitiesListPage;
